import { Injectable, NgZone, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { forceSentAnalyticsEventsInBuffer } from '@shared/store/analytics/analytics.actions';
import { fromEvent, merge } from 'rxjs';
import { filter, take, throttleTime } from 'rxjs/operators';
import * as uuid from 'uuid';

const SESSION_ID_KEY = 'SESSION_ID';
const LAST_ACTIVITY_KEY = 'LAST_ACTIVITY';
const SESSION_TIMEOUT_MS = 1800000; // 30 minutes

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  private store = inject(Store);
  private router = inject(Router);
  private ngZone = inject(NgZone);

  constructor() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        take(1),
      )
      .subscribe(() => {
        this.initTracking();
      });
  }

  initTracking(): void {
    this.initializeSession();
    this.registerListenerToSendFinalEventBatchOnBrowserTabClose();
    this.trackUserActivity();
  }

  getSessionId(): string {
    const storedSession = localStorage.getItem(SESSION_ID_KEY);
    return !storedSession || this.isSessionExpired() ? this.startNewSession() : storedSession;
  }

  private initializeSession(): void {
    const storedSession = localStorage.getItem(SESSION_ID_KEY);

    if (!storedSession || this.isSessionExpired()) {
      this.startNewSession();
    }
  }

  private startNewSession(): string {
    const newSessionId = uuid.v4();
    const now = Date.now().toString();

    localStorage.setItem(SESSION_ID_KEY, newSessionId);
    localStorage.setItem(LAST_ACTIVITY_KEY, now);

    return newSessionId;
  }

  private isSessionExpired(): boolean {
    const lastActivity = localStorage.getItem(LAST_ACTIVITY_KEY);
    if (!lastActivity) return true;

    const now = Date.now();
    const elapsed = now - parseInt(lastActivity, 10);

    return elapsed > SESSION_TIMEOUT_MS;
  }

  private refreshActivityTimestamp(): void {
    localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
  }

  private registerListenerToSendFinalEventBatchOnBrowserTabClose(): void {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.store.dispatch(forceSentAnalyticsEventsInBuffer());
      }
    });
  }

  private trackUserActivity(): void {
    this.ngZone.runOutsideAngular(() => {
      const activityEvents = ['mousemove', 'mousedown', 'keypress', 'touchstart', 'scroll'];
      merge(...activityEvents.map((event) => fromEvent(document, event)))
        .pipe(throttleTime(1000))
        .subscribe(() => {
          if (this.isSessionExpired()) {
            this.startNewSession();
          } else {
            this.refreshActivityTimestamp();
          }
        });
    });
  }
}
